import { initializeApp } from 'firebase/app';
import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  connectAuthEmulator,
  GoogleAuthProvider,
  initializeAuth,
  OAuthProvider,
} from 'firebase/auth';
import { connectFirestoreEmulator, initializeFirestore } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { buildFirebaseConfig, resolveEmulatorPort } from './firebaseConfig';
import { clientEnvironment } from 'shared/platform/environment';

const firebaseConfig = buildFirebaseConfig(
  clientEnvironment.firebase,
  typeof window !== 'undefined' ? window.location.hostname : undefined,
);

// Emulators are opt-in via env (REACT_APP_USE_EMULATORS=true), not inferred from
// hostname. `npm run dev` and the CI integration job both set it; a bare
// `npm start` leaves it unset and points at live staging.
const useEmulators = clientEnvironment.useEmulators;

// Aligned uses the canonical `(default)` Firestore database in both staging and
// production (verified via `gcloud firestore databases list`). Override only if a
// named database is introduced later.
const FIRESTORE_DATABASE_ID = clientEnvironment.firestoreDatabaseId;

const app = initializeApp(firebaseConfig);
// Firebase's default getAuth() persistence prefers IndexedDB. Its Auth
// persistence implementation closes that database on pagehide/hidden state,
// so an Auth write that races the browser lifecycle can reject with
// "Database is closing/hidden". Local-storage persistence keeps the same
// durable, cross-tab session without an IndexedDB connection to tear down.
// initializeAuth is required here so the persistence is selected before Auth
// starts restoring the current user.
const authDependencies = typeof browserPopupRedirectResolver === 'function'
  ? { persistence: browserLocalPersistence, popupRedirectResolver: browserPopupRedirectResolver }
  : { persistence: browserLocalPersistence };
export const auth = initializeAuth(app, authDependencies);
// The Firestore emulator's WebChannel transport is unreliable in some browsers
// (listeners never deliver the initial snapshot). Force long-polling against the
// emulator; in production the SDK uses WebChannel normally.
export const db = initializeFirestore(
  app,
  useEmulators ? { experimentalForceLongPolling: true } : {},
  FIRESTORE_DATABASE_ID,
);
export const storage = getStorage(app);
export const functions = getFunctions(app);
export const googleProvider = new GoogleAuthProvider();
// Aligned's social providers are Google + Apple (verified in
// firebase_authentication_service.dart). Apple requests email + name scopes.
export const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');

if (useEmulators) {
  connectAuthEmulator(auth, `http://localhost:${resolveEmulatorPort(clientEnvironment.authEmulatorPort, 9099)}`, {
    disableWarnings: true,
  });
  connectFirestoreEmulator(db, 'localhost', resolveEmulatorPort(clientEnvironment.firestoreEmulatorPort));
  connectStorageEmulator(storage, 'localhost', resolveEmulatorPort(clientEnvironment.storageEmulatorPort, 9199));
  connectFunctionsEmulator(functions, 'localhost', 5001);
}
